import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { catchError, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ClaimService } from '../../claims/claim.service';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { ConcernCausePart } from '../models/concern-cause-part';
import { PartEstimate } from '../models/estimates/part-estimate';
import { LaborEstimate } from '../models/estimates/labor-estimate';
import { HazardousMaterialEstimate } from '../models/estimates/hazardous-material-estimate';
import { ConcernCauseComponentListComponent } from '../concern-cause-component-list/concern-cause-component-list.component';
import { EstimateTotal } from '../models/estimates/estimate-total';

@Component({
  selector: 'claims-app-concerns-view',
  templateUrl: './claims-app-concerns-view.component.html'
})
export class ClaimsAppConcernsViewComponent {
  @ViewChild(ConcernCauseComponentListComponent) concernListComponent: ConcernCauseComponentListComponent;
  
  private readonly _destroying$ = new Subject<void>();

  public isLoading: boolean = true;
  public loadSuccess: boolean = false;
  public concerns: ConcernCausePart[] = [];

  constructor(private cdr: ChangeDetectorRef,
              private route: ActivatedRoute,
              private dialog: MatDialog,
              private claimService: ClaimService) {
    
  }

  ngOnInit(): void {
    // Fetch requestId from route parameters.
    this.route.paramMap
      .pipe(
        takeUntil(this._destroying$),
        map(params => params.get('id')),
        switchMap(id =>
          this.claimService.getConcerns(id)
            .pipe(takeUntil(this._destroying$),
                  catchError(() => {
                    return of([]);
                  }))
        )
      )
      .subscribe({
        next: (concerns) => {
          this.concerns = concerns;

          // Clear concer object list as it defaults to one empty item.
          this.concernListComponent.objectList = [];
          this.cdr.detectChanges(); // Let child elements render.

          this.loadSuccess = this.concerns.length > 0;

          if (this.loadSuccess) {
            this.populateForm(this.concerns);
          }
          else {
            this.showDialog('Error retrieving claim estimates',
                            'There was an error retrieving claim estimates. Please try again.',
                            'Ok',
                            undefined,
                            () => {},
                            () => {})
          }
          this.isLoading = false;
        }
      });
  }
	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

  /**
   * Populate the form with the provided concerns.
   * @param concerns The concerns to populate the form with.
   */
  public populateForm(concerns: ConcernCausePart[]): void {
    // Populate concerns.
    concerns.forEach(concern => {
      this.concernListComponent.objectList.push(concern);
      
    });
    this.cdr.detectChanges(); // Let child elements render.

    this.concernListComponent.objectForms?.forEach((concernComponent, concernIndex) => {
      const concern = concerns[concernIndex];

      // Read only.
      concernComponent.form.disable();
      
      concernComponent.setComponentGroup(concern.component?.groupName);

      concernComponent.form.patchValue({
        component: concern.component,
        componentGroup: concern.component?.groupName,
        concern: concern.customerConcern,
        cause: concern.causeOfFailure,
      });

      // Set the customer concern field separately
      this.concernListComponent.setConcernValue(concernIndex, concern.customerConcern ?? '');

      // Populate correction estimates.
      concernComponent.correctionEstimateList.objectList = [];
      concern.correctionEstimates.forEach(correction => {
        // Populate object list to update the UI.
        concernComponent.correctionEstimateList.objectList.push(correction);
      });
      this.cdr.detectChanges(); // Let child elements render.

      concernComponent.correctionEstimateList.objectForms.forEach((correctionObjectForm, correctionIndex) => {
        const correction = concern.correctionEstimates[correctionIndex];
        correctionObjectForm.form.disable();

        correctionObjectForm.form.patchValue({
          correction: correction.correction
        });
        
        // Disable drag and drop for correction estimates.
        correctionObjectForm.estimateList.enableEstimateDragAndDrop = false;
        
        // Populate part estimates.
        correctionObjectForm.estimateList.objectList = [];
        correction.partEstimates.forEach(estimate => {
          // Re-declare so the push is new reference.
          const partEstimate = new PartEstimate();
          
          partEstimate.partComponent = estimate.partComponent;
          partEstimate.partNumber = estimate.partNumber;
          partEstimate.isOem = estimate.isOem;
          partEstimate.eta = estimate.eta;
          partEstimate.price = estimate.price;
          partEstimate.quantity = estimate.quantity;
          partEstimate.taxPercentage = estimate.taxPercentage;
          partEstimate.description = estimate.description;

          correctionObjectForm.estimateList.objectList.push(partEstimate);
          this.cdr.detectChanges(); // Let child elements render.
          
          // Last element should be what was just added.
          const partForm = correctionObjectForm.estimateList.partForms.last;
          partForm.taxPercentage = partEstimate.taxPercentage;

          partForm.form.patchValue({
            component: partEstimate.partComponent,
            partNumber: partEstimate.partNumber,
            isOem: partEstimate.isOem,
            etaDays: partEstimate.eta,
            price: partEstimate.price,
            quantity: partEstimate.quantity,
            description: partEstimate.description ?? '',
          });

          if (partEstimate.description && partEstimate.description.length > 0) {
            partForm.isDescriptionOpen = true;
          }

          partForm.form.disable();
        });
        
        // Populate labor estimates.
        correction.laborEstimates.forEach(estimate => {
          // Re-declare so the push is new reference.
          const laborEstimate = new LaborEstimate();

          laborEstimate.laborOperation = estimate.laborOperation
          laborEstimate.rate = estimate.rate;
          laborEstimate.hours = estimate.hours;
          laborEstimate.taxPercentage = estimate.taxPercentage;
          laborEstimate.description = estimate.description ?? '';

          correctionObjectForm.estimateList.objectList.push(laborEstimate);
          this.cdr.detectChanges(); // Let child elements render.
          
          // Last element should be what was just added.
          const laborForm = correctionObjectForm.estimateList.laborForms.last;
          
          laborForm.form.patchValue({
            laborOperation: laborEstimate.laborOperation,
            rate: laborEstimate.rate,
            hours: laborEstimate.hours,
            tax: laborEstimate.taxPercentage,
            description: laborEstimate.description,
          });

          if (laborEstimate.description && laborEstimate.description.length > 0) {
            laborForm.isDescriptionOpen = true;
          }
          laborForm.form.disable();
        });

        // Populate material estimates.
        correction.hazardousMaterialEstimates.forEach(estimate => {
          // Re-declare so the push is new reference.
          const materialEstimate = new HazardousMaterialEstimate();

          materialEstimate.description = estimate.description ?? '';
          materialEstimate.price = estimate.price;
          materialEstimate.quantity = estimate.quantity;
          materialEstimate.taxPercentage = estimate.taxPercentage;

          correctionObjectForm.estimateList.objectList.push(materialEstimate);
          this.cdr.detectChanges(); // Let child elements render.
          
          // Last element should be what was just added.
          const materialForm = correctionObjectForm.estimateList.materialForms.last;
          materialForm.taxPercentage = materialEstimate.taxPercentage;
          
          materialForm.form.patchValue({
            description: materialEstimate.description,
            price: materialEstimate.price,
            quantity: materialEstimate.quantity,
          });
          materialForm.form.disable();
        });

      });
    });
  }

  /**
   * Get estimate totals from concern list component.
   */
  public get estimateTotal(): EstimateTotal {
    // console.count('ClaimsAppConcernsViewComponent.estimateTotal getter called');
    return this.concernListComponent.estimateTotal;
  }

  /**
   * Show confirmation dialog of provided message.
   * @param title 
   * @param messageText 
   * @param cancelButtonText 
   * @param confirmationButtonText 
   * @param onConfirmEvent 
   * @param onCancelEvent 
   */  
  public showDialog(title: string,
                    messageText: string,
                    cancelButtonText: string,
                    confirmationButtonText: string,
                    onConfirmEvent: () => void,
                    onCancelEvent: () => void): void {
      const modelDialog = this.dialog.open(DialogComponent, {
          data: {
              title: title,
              bodyText: messageText,
              cancelButtonText: cancelButtonText,
              confirmationButtonText: confirmationButtonText
          }
      });
      modelDialog.afterClosed().subscribe(result => {
          if (result) onConfirmEvent();
          else onCancelEvent();
      });
  }
}
